import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Dialog,
  DialogContent,
  TextField,
  Paper,
  useTheme,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { styled } from '@mui/material/styles';
import api from '../api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

const CompartmentQuiz = ({ vehicleId }) => {
  const navigate = useNavigate();
  useTheme(); // used for styled components/breakpoints
  const [loading, setLoading] = useState(true);
  const [selectedCompartment, setSelectedCompartment] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [score, setScore] = useState(null);
  const [correctEquipment, setCorrectEquipment] = useState([]);
  const [incorrectEquipment, setIncorrectEquipment] = useState([]);
  const [missedEquipment, setMissedEquipment] = useState([]);
  const [vehicle, setVehicle] = useState(null);
  const [error, setError] = useState(null);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isImageOpen, setIsImageOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/vehicles/${vehicleId}`);
        const data = response.data;
        
        // Stelle sicher, dass die Ausrüstung vorhanden ist
        if (!data.equipment) {
          data.equipment = [];
        }
        
        setVehicle(data);
      } catch (err) {
        console.error('Fehler beim Laden der Daten:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [vehicleId]);

  // Funktion zur Erstellung der Quiz-Ausrüstung (gleich viele richtige wie falsche)
  const createQuizEquipment = (compartmentId) => {
    if (!vehicle || !compartmentId) return [];
    
    const compartment = vehicle.compartments.find(c => c.id === compartmentId);
    if (!compartment) return [];
    
    const correctItems = compartment.equipment.map(id => parseInt(id));
    const allEquipment = vehicle.equipment || [];
    
    // Alle Equipment außer den korrekten für dieses Fach
    const incorrectItems = allEquipment
      .filter(item => !correctItems.includes(item.id))
      .map(item => item.id);
    
    // Mische und wähle gleich viele falsche wie richtige Gegenstände
    const shuffledIncorrect = incorrectItems.sort(() => Math.random() - 0.5);
    const selectedIncorrect = shuffledIncorrect.slice(0, correctItems.length);
    
    // Kombiniere richtige und falsche Gegenstände und mische sie
    const quizEquipment = [...correctItems, ...selectedIncorrect]
      .sort(() => Math.random() - 0.5);
    
    // Gib die Equipment-Objekte zurück
    return allEquipment.filter(item => quizEquipment.includes(item.id));
  };

  const handleSubmit = async () => {
    if (!selectedCompartment || !vehicle) return;

    const compartment = vehicle.compartments.find(c => c.id === selectedCompartment);
    if (!compartment) return;

    // Konvertiere die IDs zu Zahlen für den Vergleich
    const correctItems = compartment.equipment.map(id => parseInt(id));
    const selectedItems = selectedEquipment.map(id => parseInt(id));

    const correct = selectedItems.filter(id => correctItems.includes(id));
    const incorrect = selectedItems.filter(id => !correctItems.includes(id));
    const missed = correctItems.filter(id => !selectedItems.includes(id));

    setCorrectEquipment(correct);
    setIncorrectEquipment(incorrect);
    setMissedEquipment(missed);

    const totalItems = correctItems.length;
    const correctCount = correct.length;
    const incorrectCount = incorrect.length;
    const score = Math.max(0, (correctCount - incorrectCount) / totalItems * 100);
    setScore(score);

  };

  if (loading) return <div>Lade Fahrzeugdaten...</div>;
  if (error) return <div>Fehler: {error}</div>;
  if (!vehicle) return <div>Kein Fahrzeug gefunden</div>;
  const selectedCompartmentData = vehicle.compartments?.find((c) => c.id === selectedCompartment);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/')}
        sx={{ mb: 2 }}
      >
        Zurück zur Auswahl
      </Button>
      <StyledPaper elevation={3}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Gerätefach-Quiz: {vehicle.name}
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Gerätefach auswählen</InputLabel>
          <Select
            value={selectedCompartment}
            onChange={(e) => {
              const compartmentId = e.target.value;
              setSelectedCompartment(compartmentId);
              setSelectedEquipment([]);
              setScore(null);
              setCorrectEquipment([]);
              setIncorrectEquipment([]);
              setMissedEquipment([]);
              
              // Erstelle die Quiz-Ausrüstung für das gewählte Gerätefach
              const quizEquipment = createQuizEquipment(compartmentId);
              setAvailableEquipment(quizEquipment);
              setSearchTerm('');
            }}
            label="Gerätefach auswählen"
          >
            {vehicle.compartments?.map((compartment) => (
              <MenuItem key={compartment.id} value={compartment.id}>
                {compartment.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedCompartment && score === null && (
          <>
            <Typography variant="h6" gutterBottom>
              Welche Ausrüstung gehört zum Bereich {selectedCompartmentData?.name || 'dieses Gerätefachs'}?
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />
            <FormGroup>
              {availableEquipment
                .filter((item) =>
                  item.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((item) => {
                const isCorrect = correctEquipment.includes(item.id);
                const isSelected = selectedEquipment.includes(item.id);
                const showResult = score !== null;
                
                return (
                  <FormControlLabel
                    key={item.id}
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEquipment([...selectedEquipment, item.id]);
                          } else {
                            setSelectedEquipment(selectedEquipment.filter(id => id !== item.id));
                          }
                        }}
                        disabled={showResult}
                        sx={{
                          color: showResult && isSelected && isCorrect ? 'success.main' : undefined,
                          '&.Mui-checked': {
                            color: showResult && isCorrect ? 'success.main' : undefined,
                          },
                        }}
                      />
                    }
                    label={item.name}
                    sx={{
                      color: showResult && isSelected && isCorrect ? 'success.main' : undefined,
                      fontWeight: showResult && isSelected && isCorrect ? 'bold' : undefined,
                    }}
                  />
                );
              })}
            </FormGroup>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              sx={{ mt: 2 }}
              fullWidth
            >
              Auswertung
            </Button>
          </>
        )}

        {selectedCompartment && score !== null && (
          <>
            <Typography variant="h6" gutterBottom>
              Deine Auswahl:
            </Typography>
            <FormGroup>
              {availableEquipment.map((item) => {
                const isCorrect = correctEquipment.includes(item.id);
                const isSelected = selectedEquipment.includes(item.id);
                
                if (!isSelected) return null; // Zeige nur ausgewählte Items
                
                return (
                  <FormControlLabel
                    key={item.id}
                    control={
                      <Checkbox
                        checked={true}
                        disabled={true}
                        sx={{
                          color: isCorrect ? 'success.main' : 'error.main',
                          '&.Mui-checked': {
                            color: isCorrect ? 'success.main' : 'error.main',
                          },
                        }}
                      />
                    }
                    label={item.name}
                    sx={{
                      color: isCorrect ? 'success.main' : 'error.main',
                      fontWeight: isCorrect ? 'bold' : 'normal',
                    }}
                  />
                );
              })}
            </FormGroup>

            {missedEquipment.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3, color: 'warning.main' }}>
                  Verpasste Gegenstände (gehören in dieses Gerätefach):
                </Typography>
                <FormGroup>
                  {vehicle.equipment
                    .filter(item => missedEquipment.includes(item.id))
                    .map((item) => (
                      <FormControlLabel
                        key={item.id}
                        control={
                          <Checkbox
                            checked={false}
                            disabled={true}
                            sx={{
                              color: 'warning.main',
                            }}
                          />
                        }
                        label={item.name}
                        sx={{
                          color: 'warning.main',
                          fontWeight: 'bold',
                        }}
                      />
                    ))}
                </FormGroup>
              </>
            )}
          </>
        )}

        {score !== null && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" gutterBottom>
              Auswertung
            </Typography>
            <Typography variant="h6" color={score >= 70 ? 'success.main' : 'error.main'}>
              Punktzahl: {score.toFixed(1)}%
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                Richtig: {correctEquipment.length} | 
                Falsch: {incorrectEquipment.length} | 
                Verpasst: {missedEquipment.length}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
                mt: 2
              }}
            >
              {selectedCompartmentData?.image && (
                <Box
                  sx={{
                    width: 140,
                    height: 90,
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                  onClick={() => setIsImageOpen(true)}
                >
                  <Box
                    component="img"
                    src={selectedCompartmentData.image}
                    alt={selectedCompartmentData.name}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedCompartment('');
                  setSelectedEquipment([]);
                  setScore(null);
                  setCorrectEquipment([]);
                  setIncorrectEquipment([]);
                  setMissedEquipment([]);
                  setAvailableEquipment([]);
                }}
                fullWidth
              >
                Neu starten
              </Button>
            </Box>

            {selectedCompartmentData?.image && (
              <Dialog
                open={isImageOpen}
                onClose={() => setIsImageOpen(false)}
                maxWidth="md"
                fullWidth
              >
                <DialogContent sx={{ p: 0, bgcolor: 'black' }}>
                  <Box
                    component="img"
                    src={selectedCompartmentData.image}
                    alt={selectedCompartmentData.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </DialogContent>
              </Dialog>
            )}
          </Box>
        )}
      </StyledPaper>
    </Container>
  );
};

export default CompartmentQuiz; 
